import { create } from 'zustand';
import { User } from '@/types';
import { authApi } from '@/services/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  initializeSession: () => () => void;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

async function readCurrentSession() {
  const { user } = await authApi.getCurrentUser();
  return user;
}

let authRequestVersion = 0;

function beginAuthRequest() {
  authRequestVersion += 1;
  return authRequestVersion;
}

function isLatestAuthRequest(requestVersion: number) {
  return requestVersion === authRequestVersion;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  initializeSession: () => {
    let isActive = true;
    const requestVersion = beginAuthRequest();

    const applyUser = (user: User | null) => {
      if (!isActive || !isLatestAuthRequest(requestVersion)) return;
      set({
        user,
        isAuthenticated: Boolean(user),
        isLoading: false,
        error: null,
      });
    };

    const loadSession = async () => {
      set({ isLoading: true });
      try {
        const user = await readCurrentSession();
        applyUser(user);
      } catch {
        applyUser(null);
      }
    };

    void loadSession();

    return () => {
      isActive = false;
    };
  },

  login: async (email, password) => {
    const requestVersion = beginAuthRequest();
    set({ isLoading: true, error: null });
    try {
      const { user } = await authApi.login(email, password);
      if (isLatestAuthRequest(requestVersion)) {
        set({ user, isAuthenticated: true, isLoading: false, error: null });
      }
      return user;
    } catch (error) {
      const message = (error as Error).message;
      if (isLatestAuthRequest(requestVersion)) {
        set({ isLoading: false, error: message });
      }
      throw error;
    }
  },

  logout: async () => {
    const requestVersion = beginAuthRequest();
    set({ isLoading: true, error: null });
    try {
      await authApi.logout();
    } finally {
      if (isLatestAuthRequest(requestVersion)) {
        set({ user: null, isAuthenticated: false, isLoading: false, error: null });
      }
    }
  },

  setUser: (user) => {
    beginAuthRequest();
    set({
      user,
      isAuthenticated: Boolean(user),
      error: null,
    });
  },

  clearError: () => set({ error: null }),
}));
